import React from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { Visibility as ViewIcon } from "@mui/icons-material";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppDispatch } from "../../store";
import { updateCaseStatus } from "../../store/slices/casesSlice";
import { Case } from "../../services/casesService";
import StatusBadge, { getStatusLabel } from "../common/StatusBadge";
import { getCaseProgressFromStatus } from "../../utils/caseProgress";
import { useToast } from "../../hooks/useToast";

const KANBAN_COLUMN_IDS = ["pending", "in_progress", "completed", "cancelled"];

const getKanbanColumns = (t: any) =>
  KANBAN_COLUMN_IDS.map((id) => ({ id, title: getStatusLabel(t, id) }));

const PRIORITY_COLOR: Record<string, "default" | "warning" | "error"> = {
  low: "default",
  medium: "warning",
  high: "error",
};

interface SortableCaseCardProps {
  caseItem: Case;
  clientName: string;
  onView: (caseItem: Case) => void;
}

const SortableCaseCard: React.FC<SortableCaseCardProps> = ({ caseItem, clientName, onView }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: caseItem.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const progress = getCaseProgressFromStatus(caseItem.status);
  const priority = (caseItem.priority || "medium").toLowerCase();

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{ p: 1.5, mb: 1, cursor: "grab", "&:hover": { boxShadow: 2 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, pr: 1 }}>
          {caseItem.subject}
        </Typography>
        <Tooltip title={t("common.view")}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onView(caseItem);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {clientName}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Chip
          label={t(`common.${priority}`, priority)}
          size="small"
          color={PRIORITY_COLOR[priority] || "default"}
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 5, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary">
          {progress}%
        </Typography>
      </Box>
    </Paper>
  );
};

interface DroppableCaseColumnProps {
  title: string;
  status: string;
  cases: Case[];
  clients: any[];
  onView: (caseItem: Case) => void;
}

const DroppableCaseColumn: React.FC<DroppableCaseColumnProps> = ({
  title,
  status,
  cases,
  clients,
  onView,
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 2,
        minWidth: 300,
        bgcolor: isOver ? "action.hover" : "grey.50",
        transition: "background-color 0.2s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <StatusBadge status={status} />
        <Typography variant="caption" color="text.secondary">
          ({cases.length})
        </Typography>
      </Box>
      <SortableContext items={cases.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        {cases.map((caseItem) => {
          const client = clients.find((c) => c.id === caseItem.customer_id);
          return (
            <SortableCaseCard
              key={caseItem.id}
              caseItem={caseItem}
              clientName={client?.company || t("cases.unknownClient")}
              onView={onView}
            />
          );
        })}
      </SortableContext>
    </Paper>
  );
};

interface CasesKanbanProps {
  cases: Case[];
  clients: any[];
  onView: (caseItem: Case) => void;
}

const CasesKanban: React.FC<CasesKanbanProps> = ({ cases, clients, onView }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError } = useToast();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const kanbanColumns = getKanbanColumns(t);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const caseId = active.id as number;
    const newStatus = over.id as string;
    if (!KANBAN_COLUMN_IDS.includes(newStatus)) return;

    const caseItem = cases.find((c) => c.id === caseId);
    if (!caseItem || (caseItem.status || "pending") === newStatus) return;

    try {
      const result = await dispatch(updateCaseStatus({ id: caseId, status: newStatus }));
      if ("error" in result) {
        showError(t("cases.statusUpdateFailed"));
      } else {
        showSuccess(t("cases.statusUpdated"));
      }
    } catch (error) {
      showError(t("cases.statusUpdateFailed"));
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <Box sx={{ display: "flex", gap: 2, pb: 2, overflowX: "auto" }}>
        {kanbanColumns.map((column) => (
          <DroppableCaseColumn
            key={column.id}
            title={column.title}
            status={column.id}
            cases={cases.filter((c) => (c.status || "pending") === column.id)}
            clients={clients}
            onView={onView}
          />
        ))}
      </Box>
    </DndContext>
  );
};

export default CasesKanban;
